<template>
  <div
    v-if="productHydrated"
    class="B2CAccessoryProductCard relative h-full"
  >
    <div
      class="flex bg-bg-subtle"
      @mouseenter="imageHoverering = true"
      @mouseleave="imageHoverering = false"
    >
      <div
        class="relative aspect-product group overflow-hidden cursor-pointer basis-1/3 desk:basis-1/2"
      >
        <div v-if="!image">
          <img
            src="/icons/no-image.svg"
            class="object-cover"
            alt=""
          >
        </div>
        <div
          v-else
          class="w-full h-full flex items-center justify-center"
        >
          <nuxt-img
            :src="currentImage"
            provider="norce"
            preset="plpimage"
            sizes="sm:100vw desk:323px"
            class="w-full h-full object-cover opacity-100 group-hover:opacity-80 transition ease-out duration-500"
            :alt="productHydrated?.name || ''"
            loading="lazy"
          />
        </div>

        <!-- Pointer-only duplicate of the title link below; hidden from AT -->
        <nuxt-link
          :to="link"
          aria-hidden="true"
          tabindex="-1"
          @click="track"
        >
          <div
            class="imageOverlay absolute top-0 left-0 w-full h-full"
          />
        </nuxt-link>
      </div>
      <div class="basis-2/3 desk:basis-1/2 px-16 py-8 desk:px-24 desk:py-8 flex flex-col justify-center">
        <div>
          <nuxt-link
            :to="link"
            @click="track"
          >
            <div class="type-tagline text-text-subtle mb-4">{{ productHydrated.lastChildCategory?.name }}</div>
            <div class="type-headline-md mb-2">
              {{ productHydrated.name }}
            </div>
          </nuxt-link>

          <div
            ref="textRef"
            class="type-sm text-text-subtle mb-4 line-clamp-3"
            v-html="productText"
          />
          <nuxt-link
            v-if="productText && isTextClamped"
            :to="link"
            class="type-sm underline mb-8 inline-block"
            @click="track"
          >
            {{ $t('productPage.truncateTextCtaClosed') }}
          </nuxt-link>
        </div>

        <div v-if="!globalContent.disablePricingB2C" class="flex items-center gap-8 mt-8 justify-between">
          <div class="type-base">
            <ProductPrice
              :crossell="false"
              :product-item="productHydrated"
              :active-variant="quickBuyActiveVariant"
              design="plpGrid"
            />
          </div>
          <button
            v-if="productHydrated.isD2C && globalContent.getNorceCheckoutEnabled && quickBuyActiveVariant.isInStock && !userStore.isSamplesOnlyUser"
            class="btn btn--wIcon !w-32 !h-32"
            :aria-label="$t('quickbuy.button')"
            :class="{ loading: buyLoading }"
            type="button"
            @click="buyProduct"
          >
            <img
              src="/icons/cart-inv.svg"
              class="inline-block w-20"
              alt=""
            >
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductRaw } from '#constants/types/algolia';
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import { useGlobalContentStore } from '~/store/globalContent';
import { createDataLayerItem, sendDataLayer } from '~/composables/useTracking';
import useLinkReplacer from '~/composables/useLinkReplacer';
import { ref, onMounted } from 'vue';
import type { ProductVariantModel } from '#models/productVariant';
import ProductPrice from '~/components/product-page/ProductPrice.vue';
import { useCartStore } from '~/store/cart';
import { useUserStore } from '~/store/user';

const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const userStore = useUserStore();

const { productUrl } = useLinkReplacer();
const props = defineProps<{
  product: ProductRaw | ProductModel,
  useVariantImage?: boolean, // if the list from algolia is distinct = true, we show the variant image
  listIndex?: string,
}>();

defineEmits(['quickbuy']);

const productHydrated = props.product instanceof Product ? props.product : Product.create(props.product);
if (!(props.product instanceof Product)) {
  console.warn(`Using ProductCard from ProductRaw is not recommended: ${productHydrated.partNo}`);
}

const setPrimaryToActive = () => {
  if (productHydrated) {
    let primaryVariant = props.listIndex?.includes('price')
      ? productHydrated.variants.findIndex((f) => f.partNo === props.product.partNo)
      : productHydrated.variants.findIndex((f) => f.isPrimary);
    if (primaryVariant === -1) {
      primaryVariant = 0;
    }
    quickBuyActiveVariant.value = productHydrated.variants[primaryVariant];
  }
};
const quickBuyActiveVariant = ref<ProductVariantModel>(productHydrated.variants[0]);

setPrimaryToActive();

const imageHoverering = ref(false);
const image = computed(() => {
  if (props.useVariantImage && productHydrated.variantImage) {
    return productHydrated.variantImage;
  }
  // check if we have a "swatch image"
  const swatchImage = productHydrated.mediaSets.find(f => f.code === 'PrimaryImage');
  if (swatchImage && swatchImage.media[0]) {
    return swatchImage.media[0].url;
  }

  return productHydrated.listImage;
});
const hoverImage = computed(()=> {
  const hoverImage = productHydrated.mediaSets.find(f => f.code === 'InteriorImage');
  if (hoverImage && hoverImage.media[0]) {
    return hoverImage.media[0].url;
  }
  return null;
});

const currentImage = computed(()=> {
  if (imageHoverering.value && hoverImage.value) {
    return hoverImage.value;
  }
  return image.value;
});

const link = computed<string>(()=> {
  return productUrl(productHydrated.url);
});

const productText = computed(()=> {
  if (productHydrated.variants[0].variantDescription) {
    return productHydrated.variants[0].variantDescription;
  }
  if (productHydrated.shortDescription) {
    return productHydrated.shortDescription;
  }
  return productHydrated.description;

});

const textRef = ref<HTMLElement | null>(null);
const isTextClamped = ref(false);

onMounted(() => {
  if (textRef.value) {
    isTextClamped.value = textRef.value.scrollHeight > textRef.value.clientHeight;
  }
});

const buyLoading = ref(false);
const buyProduct = async() => {
  buyLoading.value = true;
  await cartStore.updateCart(
    quickBuyActiveVariant.value.partNo,
    1,
    -1,
    true,
    quickBuyActiveVariant.value
  );
  buyLoading.value = false;
};

const track = () => {
  sendDataLayer(
    'select_item',
    [ createDataLayerItem(props.product) ],
    {
      'item_list_name': globalContent.currentStory?.name,
      'item_list_id': globalContent.currentStory?.id,
    }
  );
};

</script>
